/**
 * 모바일 셸 (spec docs/superpowers/specs/2026-09-23-mobile-shell-design.md §2).
 *
 * - 공용 바텀 시트: [data-m-sheet="<id>"] 가 열고 시트 안의 [data-m-close] 가 닫는다.
 *   열기·닫기는 popover-registry 를 지나 드로어·알림·다른 시트와 배타적이고, 바깥
 *   클릭·Esc 로 닫힌다. 화면별 준비·정리는 registerSheetHooks(id, {onOpen, onClose}).
 * - 「전체」 시트 (#mAllSheet) 와 그 안의 탭 편집 (user_preferences mobile.tabs).
 * - details[data-m-fold] 는 모바일에서 접힌 채 시작한다 (데스크톱은 요약 줄이 없다).
 *
 * 탭바·「전체」 시트는 SPA 리전(m-tabs / m-all)이라 innerHTML 이 갈린다 — 리스너는
 * 전부 document 에 위임하고, 요소는 쓸 때마다 다시 찾는다. 탭 편집 중에는 그 시트에
 * data-spa-hold 를 세워 리전 교체를 미루고(calendar-history.js), 편집이 끝나면 푼다.
 * 배타 시트가 열린 동안 그 뒤의 화면은 inert 다. 안드로이드의 뒤로 가기는 열린 시트를 닫는다(close-on-back.js).
 */
import { openExclusive, closedExclusive, closePopovers, anyPopoverOpen } from './popover-registry.js';
import { savePref } from './saved-views.js';
import { showNotification } from './notifications.js';
import { navigate } from './calendar-spa.js';
import { releaseRegionHold } from './calendar-history.js';
import { phoneQuery } from './phone.js';
import { setupTouchGestures } from './touch-gestures.js';
import { setupMobileAddr } from './mobile-addr.js';
import { closeOnBack } from './close-on-back.js';

const MOBILE = phoneQuery;
const TAB_SLOTS = 4;
const openSheets = new Map(); // sheet id → { opener, onClose, exclusive, inerted, back }
const sheetHooks = new Map(); // sheet id → { onOpen, onClose }
let wired = false;
let editing = false;
let editSnapshot = '';

// 배타 시트 뒤의 화면을 inert 로 만든다 (M6): 시트에서 body 까지 올라가며 단계마다 형제를 막는다.
// 시트가 열린 뒤에 body 에 붙는 요소는 막히지 않고, 토스트 영역만 이름으로 골라 읽히도록 둔다
// (역할로 고르면 대리 보기 배너처럼 버튼을 가진 role=status 가 시트 뒤에서 잡힌다).
// 비배타 시트(일정 ⋯)는 body 에 붙는 자기 팝오버를 쓰므로 건드리지 않는다.
const INERT_SKIP = /^(SCRIPT|STYLE|TEMPLATE|LINK|META)$/;
const INERT_KEEP = '#notificationStack, .cal-undo-toast, .totp-reveal-toast, .cred-copy-toast';
function inertAround(sheet) {
    const out = [];
    for (let node = sheet; node.parentElement && node !== document.body; node = node.parentElement) {
        for (const sib of node.parentElement.children) {
            if (sib === node || sib.inert || INERT_SKIP.test(sib.tagName)) continue;
            if (sib.matches(INERT_KEEP)) continue;
            sib.inert = true;
            out.push(sib);
        }
    }
    return out;
}
function releaseInert(entry) {
    (entry.inerted || []).forEach((el) => { el.inert = false; });
    entry.inerted = [];
}

export function registerSheetHooks(id, hooks) {
    sheetHooks.set(id, hooks || {});
}

export function openSheet(sheet, opts = {}) {
    if (!sheet || !sheet.id || !sheet.hidden) return;
    const { opener = null, onOpen = null, onClose = null, exclusive = true } = opts;
    if (onOpen) onOpen(sheet);
    sheet.hidden = false;
    // 뒤로 가기는 Esc 와 같다(2026-10-02): 시트를 닫고, 비배타 시트 안의 팝오버가 열려 있으면 그것만 닫는다.
    const back = closeOnBack(() => {
        if (!exclusive && anyPopoverOpen()) { closePopovers(); return; }
        closeSheet(sheet, true);
    }, () => openSheets.has(sheet.id));
    openSheets.set(sheet.id, { opener, onClose, exclusive, inerted: exclusive ? inertAround(sheet) : [], back });
    document.body.classList.add('modal-scroll-lock');
    if (opener) opener.setAttribute('aria-expanded', 'true');
    if (exclusive) {
        openExclusive('m-sheet:' + sheet.id, () => closeSheet(sheet, true), {
            roots: () => [sheet.querySelector('.m-sheet-panel'), opener].filter(Boolean),
        });
    } else {
        // 안에서 자기 팝오버를 여는 컨트롤을 담는 시트(일정 ⋯): 등록부에 넣으면 그 팝오버의
        // openExclusive 가 시트를 닫아 버린다. 다른 팝오버만 닫고 Esc·바깥은 이 모듈이 맡는다.
        closePopovers();
    }
    const target = sheet.querySelector('.m-sheet-panel [autofocus]')
        || sheet.querySelector('.m-sheet-panel a[href], .m-sheet-panel button:not([disabled]), .m-sheet-panel input:not([disabled])');
    if (target) target.focus({ preventScroll: true });
}

export function closeSheet(sheet, restoreFocus = false) {
    if (!sheet || !openSheets.has(sheet.id)) return;
    const entry = openSheets.get(sheet.id);
    openSheets.delete(sheet.id);
    closedExclusive('m-sheet:' + sheet.id);
    entry.back.release();
    releaseInert(entry); // 포커스를 돌려주기 전에 — inert 인 요소는 포커스를 받지 않는다
    sheet.hidden = true;
    if (!openSheets.size) document.body.classList.remove('modal-scroll-lock');
    if (entry.opener) entry.opener.setAttribute('aria-expanded', 'false');
    if (entry.onClose) entry.onClose(sheet);
    if (restoreFocus && entry.opener && entry.opener.isConnected) entry.opener.focus({ preventScroll: true });
}

// 화면이 통째로 갈렸을 때(= 시트 노드가 이미 새 것일 수 있다) 등록까지 모두 지운다.
export function closeAllSheets() {
    for (const [id, entry] of Array.from(openSheets.entries())) {
        const live = document.getElementById(id);
        if (live && !live.hidden) { closeSheet(live); continue; }
        openSheets.delete(id);
        closedExclusive('m-sheet:' + id);
        entry.back.release();
        releaseInert(entry);
        if (entry.onClose) entry.onClose(null);
    }
}

function allSheet() { return document.getElementById('mAllSheet'); }

function showView(sheet, name) {
    sheet.querySelectorAll('[data-m-view]').forEach((view) => { view.hidden = view.dataset.mView !== name; });
}

function editRows(sheet) { return Array.from(sheet.querySelectorAll('[data-m-edit-list] .m-edit-row')); }

function syncLimit(sheet) {
    if (!sheet) return;
    const boxes = Array.from(sheet.querySelectorAll('.m-edit-check'));
    const on = boxes.filter((b) => b.checked).length;
    boxes.forEach((b) => { b.disabled = !b.checked && on >= TAB_SLOTS; });
    const max = sheet.querySelector('[data-m-edit-max]');
    if (max) max.hidden = on < TAB_SLOTS;
    const save = sheet.querySelector('[data-m-edit-save]');
    if (save) save.disabled = on === 0;
}

function checkedKeys(sheet) {
    return editRows(sheet).filter((li) => li.querySelector('.m-edit-check')?.checked).map((li) => li.dataset.key);
}

// 탭 편집에 들어가고 나온다. 나올 때(취소·시트 닫기·저장) 고치던 것은 버리고 격자로 돌아가며 (M1),
// 편집 중에 밀린 리전 내용이 있으면 이제 넣는다 (M3).
function enterEdit(sheet) {
    const list = sheet.querySelector('[data-m-edit-list]');
    editSnapshot = list ? list.innerHTML : '';
    editing = true;
    sheet.setAttribute('data-spa-hold', '');
    showView(sheet, 'edit');
    syncLimit(sheet);
}
function leaveEdit(sheet) {
    if (!sheet) return;
    if (editing) {
        const list = sheet.querySelector('[data-m-edit-list]');
        if (list && editSnapshot) list.innerHTML = editSnapshot;
    }
    editing = false;
    editSnapshot = '';
    showView(sheet, 'grid');
    releaseRegionHold(sheet);
}

function moveRow(button) {
    const li = button.closest('.m-edit-row');
    if (!li) return;
    if (button.dataset.mMove === 'up' && li.previousElementSibling) li.parentNode.insertBefore(li, li.previousElementSibling);
    else if (button.dataset.mMove === 'down' && li.nextElementSibling) li.parentNode.insertBefore(li.nextElementSibling, li);
    button.focus({ preventScroll: true });
}

async function saveTabs(sheet, value) {
    const buttons = sheet.querySelectorAll('[data-m-edit-save], [data-m-edit-reset], [data-m-edit-cancel]');
    buttons.forEach((b) => { b.disabled = true; });
    const saved = sheet.dataset.msgSaved || '하단 탭을 저장했습니다';
    const failed = sheet.dataset.msgFailed || '하단 탭을 저장하지 못했습니다';
    try {
        await savePref('mobile.tabs', value);
    } catch (_) {
        buttons.forEach((b) => { b.disabled = false; });
        showNotification(failed, 'error');
        return;
    }
    closeSheet(sheet);
    await navigate(window.location.href, { swap: '#mTabbar, #mAllSheet' });
    // 「전체」 버튼은 방금 새 것으로 갈렸다 — 포커스를 그리로 돌려준다 (M6)
    const all = document.getElementById('mAllBtn');
    if (all) all.focus({ preventScroll: true });
    showNotification(saved, 'success');
}

function onClick(e) {
    const opener = e.target.closest('[data-m-sheet]');
    if (opener) {
        const sheet = document.getElementById(opener.dataset.mSheet);
        if (!sheet) return;
        e.preventDefault();
        if (sheet.hidden) openSheet(sheet, Object.assign({ opener }, sheetHooks.get(sheet.id) || {}));
        else closeSheet(sheet, true);
        return;
    }
    const allBtn = e.target.closest('#mAllBtn');
    if (allBtn) {
        e.preventDefault();
        const sheet = allSheet();
        if (!sheet) return;
        if (sheet.hidden) { showView(sheet, 'grid'); openSheet(sheet, { opener: allBtn, onClose: (el) => leaveEdit(el || allSheet()) }); } else closeSheet(sheet, true);
        return;
    }
    const sheet = e.target.closest('.m-sheet');
    if (!sheet) return;
    if (e.target.closest('[data-m-close]')) { closeSheet(sheet, true); return; }
    if (sheet.id !== 'mAllSheet') return;
    if (e.target.closest('[data-m-edit]')) {
        enterEdit(sheet);
        sheet.querySelector('.m-edit-check')?.focus({ preventScroll: true });
        return;
    }
    if (e.target.closest('[data-m-edit-cancel]')) {
        leaveEdit(sheet);
        sheet.querySelector('[data-m-edit]')?.focus({ preventScroll: true });
        return;
    }
    const move = e.target.closest('[data-m-move]');
    if (move) { moveRow(move); return; }
    if (e.target.closest('[data-m-edit-reset]')) { saveTabs(sheet, ''); return; }
    if (e.target.closest('[data-m-edit-save]')) saveTabs(sheet, JSON.stringify(checkedKeys(sheet)));
}

function foldForMobile() {
    if (!MOBILE.matches) return;
    document.querySelectorAll('details[data-m-fold]:not([data-m-folded])').forEach((d) => {
        d.open = false;
        d.dataset.mFolded = '1';
    });
}

// 가상 키보드 (Review Focus 4): 키보드를 띄우는 칸에 포커스가 있으면 html.m-typing — 탭바·FAB 를 걷는다
// (CSS 는 (pointer: coarse) 에서만). select·날짜·체크 같은 칸은 키보드가 없으므로 걷지 않는다.
// 칸을 떠날 때는 지금의 누르기·탭 흐름이 끝난 **뒤에** 푼다: 누른 순간 탭바가 다시 서면 떼는 곳이 탭바가 되어
// 클릭이 두 대상의 공통 조상(body)으로 새고 바닥 근처 단추가 눌리지 않았다(Blink 는 탭의 mouseup 전에 다시
// hit-test 한다 — 손가락·마우스 모두). pointerup 과 mouseup·click 은 같은 태스크라 setTimeout 0 이면 그 뒤다.
const KEYBOARD_FIELD = 'input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="url"], input[type="tel"], input[type="password"], input[type="number"], textarea, [contenteditable="true"], [contenteditable=""]';
function keyboardField(el) {
    return !!(el && el.matches && el.matches(KEYBOARD_FIELD) && !el.readOnly && !el.disabled);
}
function wireTyping() {
    const root = document.documentElement;
    let pressed = false;
    let pending = false;
    const sync = () => { pending = false; root.classList.toggle('m-typing', keyboardField(document.activeElement)); };
    const later = () => setTimeout(sync, 0);
    document.addEventListener('focusin', (e) => { if (keyboardField(e.target)) sync(); }, true);
    document.addEventListener('focusout', () => { if (pressed) pending = true; else later(); }, true);
    document.addEventListener('pointerdown', () => { pressed = true; }, true);
    const release = () => { pressed = false; if (pending) later(); };
    document.addEventListener('pointerup', release, true);
    document.addEventListener('pointercancel', release, true);
    sync();
}

export function setupMobileShell() {
    foldForMobile();
    // 제스처(시트 끌어 닫기·드로어·기간 넘기기·당겨서 새로고침, spec 2026-09-25-phone-landscape §E) — 새 패널은 매번 묶는다
    setupTouchGestures({ closeSheet });
    if (wired) return;
    wired = true;
    setupMobileAddr(); // 폰 설치 앱의 주소 줄 (2026-09-30) — 설치 앱이 아니면 아무것도 묶지 않는다
    wireTyping();
    document.addEventListener('click', onClick);
    document.addEventListener('change', (e) => {
        if (e.target.matches && e.target.matches('#mAllSheet .m-edit-check')) syncLimit(allSheet());
    });
    // 열린 시트 안에서 Tab 을 가둔다 (spec §2.9): 끝에서 감고, 포커스가 어디에도 없으면(body) 시트 안으로
    // 들인다 (M6). 시트가 열린 뒤에 뜬 다른 요소(확인창·칩 팝오버, body 에 붙는다) 안의 포커스는 건드리지
    // 않는다 — 그 요소가 제 Tab 을 가진다.
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab' || !openSheets.size) return;
        const [id, entry] = Array.from(openSheets.entries()).pop();
        const panel = document.getElementById(id)?.querySelector('.m-sheet-panel');
        if (!panel) return;
        const items = Array.from(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea')).filter((el) => el.getClientRects().length);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (!panel.contains(active)) {
            if (active && active !== document.body && active !== document.documentElement) return;
            (e.shiftKey ? last : first).focus();
            e.preventDefault();
        } else if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
        else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    });
    // 비배타 시트의 Esc: 그 안의 팝오버·메뉴가 열려 있으면 그것만 닫히게 둔다. window 캡처라 문서에 붙은
    // 어느 Esc 처리기(등록부·컨텍스트 메뉴)보다 먼저 판단한다 — 붙는 순서에 기대지 않는다 (M5).
    window.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || anyPopoverOpen()) return;
        const top = Array.from(openSheets.entries()).reverse().find(([, v]) => !v.exclusive);
        const el = top && document.getElementById(top[0]);
        if (el) { e.preventDefault(); closeSheet(el, true); }
    }, true);
    // 뒤로 가기 캐시(bfcache)에서 돌아온 문서는 떠날 때 열려 있던 시트를 그대로 들고 온다 (M15).
    window.addEventListener('pageshow', (e) => { if (e.persisted) closeAllSheets(); });
    // 페이지가 갈리면 시트는 닫는다 (같은 경로의 부분 갱신 — 필터 바꾸기 — 에는 열어 둔다).
    document.addEventListener('calendar:page-swapped', () => { closeAllSheets(); foldForMobile(); });
    // 이동이 끝나면 시트를 닫는다 (spec §2.8). 같은 경로의 부분 갱신(예: 일정 ⋯ 시트 안의 필터 바꾸기)은
    // 비배타 시트만 열어 둔다 — 「전체」 에서 지금 있는 모듈을 다시 골라도 그 시트는 닫혀야 한다 (리뷰 I3).
    document.addEventListener('calendar:navigated', (e) => {
        if (e.detail && e.detail.crossPath) { closeAllSheets(); return; }
        for (const [id, entry] of Array.from(openSheets.entries())) {
            const live = document.getElementById(id);
            if (entry.exclusive && live) closeSheet(live);
        }
    });
    MOBILE.addEventListener('change', (ev) => {
        if (ev.matches) { foldForMobile(); return; }
        closeAllSheets();
        document.querySelectorAll('details[data-m-fold]').forEach((d) => { d.open = true; delete d.dataset.mFolded; });
    });
}
